// Lightweight, privacy-first behavioral tracking for Planet G.
// Uses fetch keepalive so events flush even on page unload.
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

function backendBase() {
  return (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
}

function lsGet(k, fallback) { try { return localStorage.getItem(k) || fallback; } catch { return fallback; } }
function lsSet(k, v) { try { localStorage.setItem(k, v); } catch { /* silent */ } }
function ssGet(k, fallback) { try { return sessionStorage.getItem(k) || fallback; } catch { return fallback; } }
function ssSet(k, v) { try { sessionStorage.setItem(k, v); } catch { /* silent */ } }

function uid() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return 'v_' + Math.random().toString(36).slice(2) + Date.now();
}

function detectDevice() {
  const w = window.innerWidth || 1024;
  if (w < 640) return 'mobile';
  if (w < 1024) return 'tablet';
  return 'desktop';
}

function sessionInfo() {
  let visitor_id = lsGet('pg_visitor_id', null);
  if (!visitor_id) { visitor_id = uid(); lsSet('pg_visitor_id', visitor_id); }
  let session_id = ssGet('pg_session_id', null);
  if (!session_id) { session_id = uid(); ssSet('pg_session_id', session_id); }
  return { visitor_id, session_id };
}

function utmParams() {
  try {
    const p = new URLSearchParams(window.location.search);
    return {
      utm_source: p.get('utm_source') || undefined,
      utm_medium: p.get('utm_medium') || undefined,
      utm_campaign: p.get('utm_campaign') || undefined,
    };
  } catch { return {}; }
}

let _queue = [];
let _flushTimer = null;

async function flush(force = false) {
  if (_queue.length === 0) return;
  const batch = _queue.splice(0, 20);
  const base = backendBase();
  try {
    await fetch(`${base}/api/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ events: batch }),
      keepalive: force,
    });
  } catch { /* swallow; analytics must never break UX */ }
}

export function trackEvent(event) {
  const { visitor_id, session_id } = sessionInfo();
  const enriched = {
    visitor_id, session_id,
    referrer: document.referrer || '',
    device: detectDevice(),
    path: window.location.pathname,
    ...utmParams(),
    ...event,
  };
  _queue.push(enriched);
  if (!_flushTimer) {
    _flushTimer = setTimeout(() => { _flushTimer = null; flush(); }, 800);
  }
}

// Flush on unload
if (typeof window !== 'undefined' && !window.__pgTrackerBound) {
  window.__pgTrackerBound = true;
  window.addEventListener('pagehide', () => flush(true));
  window.addEventListener('beforeunload', () => flush(true));
}

/**
 * <PGTracker /> — mounts once inside <BrowserRouter> and:
 *   - Records a page_view on every route change (marking first as landing_page).
 *   - Tracks scroll depth milestones (25/50/75/100).
 *   - Tracks time_on_page every 15s and on navigation.
 *   - Emits exit_intent (desktop only) on mouseleave near top.
 */
export default function PGTracker() {
  const location = useLocation();
  const isFirst  = useRef(true);
  const pageStart = useRef(Date.now());
  const scrollHits = useRef(new Set());

  // Page view + reset
  useEffect(() => {
    const isLanding = isFirst.current;
    isFirst.current = false;
    pageStart.current = Date.now();
    scrollHits.current = new Set();
    trackEvent({ event_type: 'page_view', landing_page: isLanding });
    return () => {
      const dur = Date.now() - pageStart.current;
      if (dur > 1000) trackEvent({ event_type: 'time_on_page', duration_ms: dur });
    };
  }, [location.pathname]);

  // Scroll depth
  useEffect(() => {
    const onScroll = () => {
      const h = document.documentElement;
      const scrolled = h.scrollTop + window.innerHeight;
      const full = Math.max(h.scrollHeight, 1);
      const pct = Math.min(100, Math.round((scrolled / full) * 100));
      for (const mark of [25, 50, 75, 100]) {
        if (pct >= mark && !scrollHits.current.has(mark)) {
          scrollHits.current.add(mark);
          trackEvent({ event_type: 'scroll', scroll_pct: mark });
        }
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [location.pathname]);

  // Time-on-page beacon every 15s
  useEffect(() => {
    const t = setInterval(() => {
      const dur = Date.now() - pageStart.current;
      if (dur > 5000) trackEvent({ event_type: 'time_on_page', duration_ms: dur });
    }, 15000);
    return () => clearInterval(t);
  }, [location.pathname]);

  // Exit intent (desktop only)
  useEffect(() => {
    if (detectDevice() !== 'desktop') return undefined;
    const onLeave = (e) => { if (e.clientY <= 3) trackEvent({ event_type: 'exit_intent' }); };
    document.addEventListener('mouseout', onLeave);
    return () => document.removeEventListener('mouseout', onLeave);
  }, []);

  return null;
}
